import * as React from 'react';

/** Overlay choice (Dialog vs Drawer) flips here. */
const MOBILE_BREAKPOINT = 768;
/**
 * App chrome (sidebar vs bottom tabs) flips at 1024px on the `@container/chrome`
 * shell so a device never shows both the desktop sidebar and the mobile tab bar.
 * Keep in sync with `@lg/chrome:` classes in ui/sidebar.tsx and the org layout.
 */
export const MOBILE_CHROME_BREAKPOINT = 1024;

export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined
  );

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      setIsMobile(window.innerWidth < breakpoint);
      return;
    }
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    mql.addEventListener('change', onChange);
    setIsMobile(window.innerWidth < breakpoint);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpoint]);

  return !!isMobile;
}

/**
 * Tracks whether the app chrome shell is in mobile mode by observing the
 * `@container/chrome` wrapper width (not the viewport).
 */
export function useChromeMobile() {
  const [isChromeMobile, setIsChromeMobile] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  const ref = React.useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    elementRef.current = node;
    if (!node) return;

    const update = () => {
      setIsChromeMobile(node.clientWidth < MOBILE_CHROME_BREAKPOINT);
    };
    update();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(node);
  }, []);

  return { ref, isChromeMobile, elementRef };
}
